import React from 'react';
import OnePlayer from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/OnePlayer";
import type { IPlayerInLines } from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/PlayersInLines";
import type { IPlayer } from "@ihs-widget/components/StatsConsole/Widgets/LineupWidget/parts/Players";

interface DiamondFormationPlayersInLinesProps {
    players: IPlayerInLines[];
    rowIndex: number;
    team: 'home' | 'away';
    iframeWidth?: number | null;
}

// Helper function to organize players in diamond formation
export const organizeDiamondFormation = (players: IPlayerInLines[]): { 
    am: IPlayerInLines | null, 
    cm1: IPlayerInLines | null, 
    cm2: IPlayerInLines | null, 
    dm: IPlayerInLines | null 
} | null => {
    if (players.length !== 4) return null;

    let am: IPlayerInLines | null = null;
    let cm1: IPlayerInLines | null = null;
    let cm2: IPlayerInLines | null = null;
    let dm: IPlayerInLines | null = null;

    // Identify players by position
    for (const player of players) {
        const position = player.position?.toLowerCase() || '';
        if (position.includes('attacking_midfielder') || position.includes('attacking midfielder')) {
            am = player;
        } else if (position.includes('defensive_midfielder') || position.includes('defensive midfielder')) {
            dm = player;
        } else if (position.includes('central_midfielder') || position.includes('midfielder')) {
            if (!cm1) {
                cm1 = player;
            } else if (!cm2) {
                cm2 = player;
            }
        }
    }

    // If not all positions are found, use the order from the data
    if (!am || !dm || !cm1 || !cm2) {
        // Fallback: use the order from the data (first = AM, second and third = CM, fourth = DM)
        am = players[0];
        cm1 = players[1];
        cm2 = players[2];
        dm = players[3];
    }

    return { am, cm1, cm2, dm };
};

const DiamondFormationPlayersInLines = ({ players, rowIndex, team, iframeWidth }: DiamondFormationPlayersInLinesProps) => {
    const diamond = organizeDiamondFormation(players);
    if (!diamond || !diamond.am || !diamond.cm1 || !diamond.cm2 || !diamond.dm) {
        return null;
    }

    return (
        <div data-testid="diamond-formation-players-in-lines">
            {/* Line 1: AM (Attacking Midfielder) - centered */}
            <div key={`${rowIndex}-diamond-am-${team}`} className={`flex justify-center mb-[8px] w-full flex-nowrap
                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
            `}>
                <OnePlayer
                    team={team}
                    key={`${rowIndex}-0-${diamond.am?.name}-${diamond.am.number}-${team}`}
                    width={iframeWidth && iframeWidth < 349 ? `${iframeWidth - 24}px` : '343px'}
                    player={diamond.am as IPlayer}
                    id={`${rowIndex}-0-${diamond.am?.name}-${diamond.am.number}-${team}`}
                    longName={true}
                    isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                    isLineup={true}
                />
            </div>
            {/* Line 2: CM, CM (Central Midfielder) - left/right */}
            <div key={`${rowIndex}-diamond-cm-${team}`} className={`flex justify-center mb-[8px] w-full flex-nowrap
                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
            `}>
                <OnePlayer
                    team={team}
                    key={`${rowIndex}-1-${diamond.cm1?.name}-${diamond.cm1.number}-${team}`}
                    width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 2}px` : '169.5px'}
                    player={diamond.cm1 as IPlayer}
                    id={`${rowIndex}-1-${diamond.cm1?.name}-${diamond.cm1.number}-${team}`}
                    longName={true}
                    isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                    isLineup={true}
                />
                <OnePlayer
                    team={team}
                    key={`${rowIndex}-2-${diamond.cm2?.name}-${diamond.cm2.number}-${team}`}
                    width={iframeWidth && iframeWidth < 349 ? `${(iframeWidth - 24) / 2}px` : '169.5px'}
                    player={diamond.cm2 as IPlayer}
                    id={`${rowIndex}-2-${diamond.cm2?.name}-${diamond.cm2.number}-${team}`}
                    longName={true}
                    isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                    isLineup={true}
                />
            </div>
            {/* Line 3: DM (Defensive Midfielder) - centered */}
            <div key={`${rowIndex}-diamond-dm-${team}`} className={`flex justify-center mb-[8px] w-full flex-nowrap
                ${iframeWidth && iframeWidth < 349 ? 'gap-px' : 'gap-[4px]'}
            `}>
                <OnePlayer
                    team={team}
                    key={`${rowIndex}-3-${diamond.dm?.name}-${diamond.dm.number}-${team}`}
                    width={iframeWidth && iframeWidth < 349 ? `${iframeWidth - 24}px` : '343px'}
                    player={diamond.dm as IPlayer}
                    id={`${rowIndex}-3-${diamond.dm?.name}-${diamond.dm.number}-${team}`}
                    longName={true}
                    isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                    isLineup={true}
                />
            </div>
        </div>
    );
};

export default DiamondFormationPlayersInLines;

